import { useContext, useEffect } from 'react';
import { Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import EditVersionContext from '@/context/EditVersionContext/EditVersionContext.tsx';
import { StaticDataset } from '@/types/datasets.ts';
import { AutomationVersion } from '@/types/automations.ts';
import { get_automation_version_url } from '@/utils/urls.ts';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';

export default function StaticReplacementStatus({ source }: { source: StaticDataset }) {
  const api = useApiClient();
  const editor = useContext(EditVersionContext);
  const operation = source.editor_replacement;
  const registered = !!operation && !!editor?.isRegisteredReplacement?.(operation.id);
  const result = useQuery({
    queryKey: ['static-editor-result', operation?.id, operation?.result_version_id],
    queryFn: async () =>
      (await api.get<AutomationVersion>(get_automation_version_url(operation!.result_version_id)))
        .data,
    enabled: operation?.status === 'applied' && !!operation.result_version_id && registered,
  });
  useEffect(() => {
    if (registered && operation && result.data)
      editor?.acceptReplacement?.(operation.id, result.data);
  }, [registered, operation, result.data, editor]);
  if (!operation || ['available', 'superseded'].includes(operation.status)) return null;
  if (operation.status === 'applied') {
    if (!registered) return null;
    if (result.isError || result.fetchStatus === 'paused')
      return (
        <Stack gap="xs">
          <Text size="xs" c="red" role="alert">
            Replacement is ready, but the updated draft couldn’t be loaded.
          </Text>
          <StyledButton
            variant="outline"
            size="sm"
            onClick={() => void result.refetch()}
            disabled={result.isFetching}
          >
            Retry loading draft
          </StyledButton>
        </Stack>
      );
    if (!result.data)
      return (
        <Text size="xs" c="dimmed" role="status">
          Replacement is ready. Loading the updated draft…
        </Text>
      );
    if (editor?.hasPendingEdits)
      return (
        <Text size="xs" c="dimmed" role="status">
          Replacement is ready. Waiting for your edits to finish saving…
        </Text>
      );
    return (
      <Stack gap="xs">
        <Text size="xs" c="dimmed" role="status">
          Replacement is ready. Reload the editor to view the latest draft.
        </Text>
        <StyledButton variant="outline" size="sm" onClick={editor?.reloadEditor}>
          Reload editor
        </StyledButton>
      </Stack>
    );
  }
  if (operation.status === 'processing')
    return (
      <Text size="xs" c="dimmed" role="status">
        Processing replacement… Existing draft data is unchanged.
      </Text>
    );
  return (
    <Text size="xs" c="red" role="alert">
      {operation.status === 'conflict'
        ? 'The draft’s data changed during processing. Existing draft data was kept. Refresh and replace again.'
        : 'Replacement could not be completed. Existing draft data was kept. Try replacing the file again.'}
    </Text>
  );
}
